import React from "react";
import { Box, Typography, Chip, Stack } from "@mui/material";
import {
  Language as LanguageIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Reply as ReplyIcon,
  OpenInNew as OpenInNewIcon,
  Image as ImageIcon
} from "@mui/icons-material";
import PhoneMockup from "./PhoneMockup"; 

export default function RCSTemplatePreview({ template, bot }) {
  if (!template) return <PhoneMockup botName={bot?.bot_name} botLogo={bot?.bot_logo} />;

  // Safely parse suggestions
  let suggestions = [];
  try {
    suggestions = typeof template.suggestions === 'string' 
      ? JSON.parse(template.suggestions) 
      : (template.suggestions || []);
  } catch (e) { console.error("Error parsing suggestions"); }

  let carouselList = [];
  try {
    carouselList = typeof template.carousel_list === 'string'
      ? JSON.parse(template.carousel_list)
      : (template.carousel_list || []);
  } catch (e) { console.error("Error parsing carousel"); }

  // 🟢 NEW: Safely get the media URL (checks both uploaded file and direct URL)
  const templateMediaUrl = template.media_file || template.document_url;

  const getSuggestionIcon = (actionType) => {
    switch (actionType) {
      case "url_action": return <LanguageIcon sx={{ fontSize: 11 }} />;
      case "dialer_action": return <PhoneIcon sx={{ fontSize: 11 }} />;
      case "view_location_latlong": 
      case "view_location_query": return <LocationIcon sx={{ fontSize: 11 }} />;
      case "reply": return <ReplyIcon sx={{ fontSize: 11 }} />;
      default: return <OpenInNewIcon sx={{ fontSize: 11 }} />;
    }
  };

  const renderSuggestions = (sugs, isVertical = false) => {
    if (!sugs || sugs.length === 0) return null;
    return (
      <Box sx={{ display: "flex", flexDirection: isVertical ? "column" : "row", flexWrap: isVertical ? "nowrap" : "wrap", gap: "5px", mt: 1 }}>
        {sugs.map((sug, idx) => (
          <Box key={idx} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: 1.25, py: "4px", border: "1.5px solid #128c7e", borderRadius: "20px", bgcolor: "rgba(255,255,255,0.9)", width: isVertical ? "100%" : "auto", justifyContent: isVertical ? "center" : "flex-start" }}>
            <Box sx={{ color: "#128c7e", display: "flex", alignItems: "center" }}>
              {getSuggestionIcon(sug.action_type || sug.suggestionType)}
            </Box>
            <Typography sx={{ fontSize: "10px", fontWeight: 600, color: "#128c7e", whiteSpace: isVertical ? "nowrap" : "normal" }}>
              {sug.text || sug.displayText || `Action ${idx + 1}`}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <PhoneMockup botName={bot?.bot_name} botLogo={bot?.bot_logo}>
      
      {/* ── 🟢 NEW: Media Template (Image at Top) ── */}
      {(template.template_type === "text_message_with_media" || template.template_type === "text_message_with_pdf") && templateMediaUrl && template.message_order !== "text_message_at_top" && (
         <Box sx={{ borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", mb: 0.5, width: "100%" }}>
           <Box component="img" src={templateMediaUrl} sx={{ width: "100%", height: 160, objectFit: "cover", display: "block" }} />
         </Box>
      )}

      {/* ── Text Message Bubble ── */}
      {template.text_message_content && (
        <Box sx={{ alignSelf: "flex-start", maxWidth: "90%", mb: 1 }}>
          <Box sx={{ bgcolor: "#fff", borderRadius: "4px 16px 16px 16px", px: 1.5, py: 1, boxShadow: "0 1px 4px rgba(0,0,0,0.12)" }}>
            <Typography sx={{ fontSize: "12px", color: "#1a1a1a", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
              {template.text_message_content}
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── 🟢 NEW: Media Template (Image at Bottom) ── */}
      {(template.template_type === "text_message_with_media" || template.template_type === "text_message_with_pdf") && templateMediaUrl && template.message_order === "text_message_at_top" && (
         <Box sx={{ borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", mt: 0.5, mb: 1, width: "100%" }}>
           <Box component="img" src={templateMediaUrl} sx={{ width: "100%", height: 160, objectFit: "cover", display: "block" }} />
         </Box>
      )}

      {/* ── 🟢 UPDATED: Rich Card (Now checks templateMediaUrl) ── */}
      {template.template_type === "rich_card" && (
        <Box sx={{ borderRadius: "12px", overflow: "hidden", bgcolor: "#fff", boxShadow: "0 3px 12px rgba(0,0,0,0.15)", width: "100%", mb: 1 }}>
          {templateMediaUrl ? (
            <Box component="img" src={templateMediaUrl} sx={{ width: "100%", height: 140, objectFit: "cover", display: "block" }} />
          ) : (
            <Box sx={{ width: "100%", height: 120, background: "linear-gradient(135deg, #e0e0e0, #f5f5f5)", display: "flex", alignItems: "center", justifyContent: "center" }}><ImageIcon sx={{ color: "#bbb", fontSize: 28 }} /></Box>
          )}
          <Box sx={{ p: 1.25 }}>
            <Typography sx={{ fontSize: "12px", fontWeight: 700, mb: 0.5 }}>{template.card_title}</Typography>
            <Typography sx={{ fontSize: "10px", color: "#666", lineHeight: 1.4 }}>{template.card_description}</Typography>
            {renderSuggestions(suggestions, true)}
          </Box>
        </Box>
      )}

      {/* ── Carousel ── */}
      {template.template_type === "carousel" && carouselList.length > 0 && (
        <Box sx={{ width: "100%", overflowX: "auto", pb: 1, "&::-webkit-scrollbar": { display: "none" } }}>
          <Box sx={{ display: "flex", gap: "8px", width: "max-content" }}>
            {carouselList.map((card, i) => (
              <Box key={i} sx={{ width: 200, flexShrink: 0, borderRadius: "10px", overflow: "hidden", bgcolor: "#fff", boxShadow: "0 2px 10px rgba(0,0,0,0.14)", display: "flex", flexDirection: "column" }}>
                <Box sx={{ height: 120 }}>
                  {card.media_url ? <Box component="img" src={card.media_url} sx={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <Box sx={{ width: "100%", height: "100%", bgcolor: "#e8e8e8" }} />}
                </Box>
                <Box sx={{ p: 1, flex: 1 }}>
                  <Typography sx={{ fontSize: "11px", fontWeight: 700, mb: 0.25 }}>{card.title}</Typography>
                  <Typography sx={{ fontSize: "9px", color: "#888", lineHeight: 1.4 }}>{card.description}</Typography>
                </Box>
                {card.suggestions?.length > 0 && (
                  <Box sx={{ px: 1, pb: 1 }}>{renderSuggestions(card.suggestions, true)}</Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* ── Standard Suggestions (Non-Card) ── */}
      {template.template_type !== "rich_card" && template.template_type !== "carousel" && suggestions.length > 0 && (
        <Box sx={{ alignSelf: "flex-start", width: "100%" }}>
          {renderSuggestions(suggestions, false)}
        </Box>
      )}

    </PhoneMockup>
  );
}